import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import actions, { navigate } from '../actions';
import {
    autocompleteSearchBar as autocompleteStyle,
    searchBar as style
} from '../styles/input';
import { withState } from './_hoc';
import { Link } from './Button';
import { RText } from './elements';
import { Icon } from './Icon';
import {
    InputWrapper,
    TextInput
} from './input';
import { SectionList } from './List';
import {
    Platform,
    SearchBarNative,
    TouchableWithoutFeedback,
    View
} from './react';
import { ErrorStub } from './Stub';
import { TextHighliter } from './TextHighliter';

const SuggestionsListItem = ({
    item,
    onPress,
    keyword,
}) => (
    <TouchableWithoutFeedback onPress={onPress}>
        <View style={autocompleteStyle.suggestionsItem}>
            <TextHighliter
                value={item.name}
                keyword={keyword}
                containerStyle={autocompleteStyle.suggestionsItemTextContainer}
                style={autocompleteStyle.suggestionsItemText}
            />
        </View>
    </TouchableWithoutFeedback>
);

export const getNormalizedKeyword = (kw) => {
    if ((kw ?? '').includes('-')) {
        return `${kw.split('-')?.[0]}-`;
    }
    return kw;
};

export const AutocompleteSearchBar = withState(
    ({
        isLoading,
        keyword,
        shouldReload,
        placeholder,
        value = keyword,
        onClear,
        onChangeText,
        suggestions = [],
        setState,
        onSuggestionSelected,
        isShowingSuggestions,
        errorOccurred,
        autoCorrect = true,
        routeToNav = 'DrugsList',
        manualVisit,
        inputRef: externalInputRef,
    }) => {
        // На iOS:
        //  - zIndex:1 всегда (не меняется при рендере — исключает reorder native views)
        //  - высота контейнера никогда не меняется (нет height:'100%')
        //  - панель подсказок позиционируется абсолютно и ВСЕГДА присутствует в native-иерархии
        //    (скрыта через opacity:0 + pointerEvents:'none'), чтобы исключить добавление/удаление
        //    native views при вводе 3-го символа
        //  - useEffect восстанавливает фокус (страховка) с минимальной задержкой
        // На Android поведение не изменилось.
        const isIOS = Platform.OS === 'ios';
        const { height: windowHeight } = useWindowDimensions();
        const insets = useSafeAreaInsets();
        const searchBarWrapperRef = useRef(null);
        const [ iosSuggestionsPanelHeight, setIosSuggestionsPanelHeight ] = useState(null);

        const updateIosSuggestionsPanelHeight = useCallback(() => {
            if (!isIOS) return;
            const node = searchBarWrapperRef.current;
            if (!node?.measureInWindow) return;
            node.measureInWindow((x, y, width, height) => {
                const h = windowHeight - (y + height) - insets.bottom;
                if (h > 0) {
                    setIosSuggestionsPanelHeight(h);
                }
            });
        }, [ isIOS,
            windowHeight,
            insets.bottom ]);

        useEffect(() => {
            if (!isIOS) return;
            const id = requestAnimationFrame(() => {
                updateIosSuggestionsPanelHeight();
            });
            return () => cancelAnimationFrame(id);
        }, [ isIOS,
            updateIosSuggestionsPanelHeight,
            isShowingSuggestions,
            isLoading ]);

        const containerStyle = isIOS
            ? { zIndex: 1 }
            : (isShowingSuggestions ? { height: '100%' } : {});

        if (shouldReload) {
            onChangeText(keyword);
            actions.setSearchShouldReload(false);
        }

        const internalInputRef = useRef(null);
        const inputRef = externalInputRef || internalInputRef;

        // Высота строки поиска для абсолютного позиционирования на iOS
        const [searchBarHeight, setSearchBarHeight] = useState(0);

        // Страховочный useEffect: восстанавливает фокус, если iOS всё же скрыла клавиатуру
        // (например, при изменении zIndex или isLoading). Задержка 50ms вместо 100ms —
        // флик клавиатуры менее заметен. При корректной работе opacity-подхода не срабатывает.
        const prevShowingRef = useRef(isShowingSuggestions);
        const prevLoadingRef = useRef(isLoading);
        useEffect(() => {
            const showingChanged = prevShowingRef.current !== isShowingSuggestions;
            const loadingChanged = prevLoadingRef.current !== isLoading;
            prevShowingRef.current = isShowingSuggestions;
            prevLoadingRef.current = isLoading;
            const layoutChanged = showingChanged || (loadingChanged && isShowingSuggestions);
            if (isIOS && layoutChanged) {
                const timer = setTimeout(() => {
                    inputRef.current?.focus?.();
                }, 50);
                return () => clearTimeout(timer);
            }
        }, [ isShowingSuggestions, isLoading ]);

        const suggestionsPanel = (
            <SectionList
                white
                title="drugs"
                type="drugs-suggestions"
                listContainerStyle={{ flexGrow: 0 }}
                isLoading={isLoading}
                loadingIndicatorStyle={autocompleteStyle.loadingIndicatorStyle}
                count={suggestions && suggestions.length}
                data={suggestions}
                renderItem={({ item }) => (
                    <SuggestionsListItem
                        item={item}
                        keyword={getNormalizedKeyword(value)}
                        onPress={() => {
                            onSuggestionSelected(item.name);
                            setState({
                                value: item.name,
                                suggestions: [],
                                isShowingSuggestions: false,
                            });
                        }}
                    />
                )}
                noscroll
                footer={
                    <View
                        style={{
                            paddingVertical: 8,
                            borderTopWidth: 1,
                            borderTopColor: '#E0E0E0',
                        }}
                    >
                        <Link
                            noflex
                            href={() => {
                                if (value.trim().length > 2) {
                                    navigate(routeToNav, {
                                        keyword: value.trim(),
                                        drugsList: null,
                                    });
                                }
                            }}
                            underline
                            id="titles.advanced_search"
                        />
                        <RText
                            id="hints.advanced_search"
                            style={{ marginTop: 2 }}
                        />
                    </View>
                }
            />
        );

        // iOS: панель подсказок и ошибок всегда в native-иерархии.
        // Скрыта через opacity:0 + pointerEvents:'none' когда не нужна.
        // Это предотвращает добавление/удаление native views при вводе 3-го символа,
        // что устраняет главную причину скрытия клавиатуры на iOS.
        const showSuggestions = isShowingSuggestions && !errorOccurred;
        const iosAbsoluteBase = {
            position: 'absolute',
            top: searchBarHeight + 20,
            left: 0,
            right: 0,
            zIndex: 10,
            backgroundColor: 'white',
        };

        return (
            <View style={containerStyle}>
                <View
                    ref={isIOS ? searchBarWrapperRef : undefined}
                    style={[ style.searchBar, { zIndex: 2 } ]}
                    onLayout={isIOS
                        ? ({ nativeEvent: { layout } }) => {
                            setSearchBarHeight(layout.height);
                            requestAnimationFrame(() => updateIosSuggestionsPanelHeight());
                        }
                        : undefined}
                >
                    <InputWrapper>
                        <SearchBarNative
                            ref={inputRef}
                            autoCorrect={autoCorrect}
                            accessibilityLabel="search"
                            searchIcon={Icon.SearchBarFind}
                            clearIcon={value ? <Icon.SearchBarClose onPress={onClear}/> : null}
                            platform="default"
                            placeholder={placeholder}
                            containerStyle={{
                                ...style.container,
                                borderRadius: 3,
                                backgroundColor: 'transparent',
                                borderTopColor: 'transparent',
                                borderBottomColor: 'transparent',
                                width: '100%',
                                padding: 0,
                                margin: 0,
                                paddingRight: 0,
                            }}
                            inputStyle={style.input}
                            inputContainerStyle={[ style.input, { paddingVertical: 0 } ]}
                            rightIconContainerStyle={value ? { width: 30 } : {}}
                            leftIconContainerStyle={{ width: 30 }}
                            value={value || keyword}
                            onCancel={onClear}
                            {...(Platform.OS === 'web' ? {} : { onClear })}
                            onChangeText={onChangeText}
                            underlineColorAndroid="transparent"
                            onSubmitEditing={() => {
                                if (value.trim().length > 2) {
                                    navigate(routeToNav, {
                                        keyword: value.trim(),
                                        drugsList: null,
                                        manualVisit,
                                    });
                                }
                            }}
                            InputComponent={TextInput}
                        />
                    </InputWrapper>
                </View>
                {isIOS ? (
                    // iOS: ErrorStub всегда в native-иерархии, показывается через opacity
                    <View
                        pointerEvents={errorOccurred ? 'auto' : 'none'}
                        style={[ iosAbsoluteBase, { opacity: errorOccurred ? 1 : 0 } ]}
                    >
                        <ErrorStub
                            error={Object.R('titles.errorSearchDrugs')}
                            style={autocompleteStyle.errorPanel}
                            support
                        />
                    </View>
                ) : (errorOccurred ? (
                    <ErrorStub
                        error={Object.R('titles.errorSearchDrugs')}
                        style={autocompleteStyle.errorPanel}
                        support
                    />
                ) : null)}
                {isIOS ? (
                    // iOS: SectionList всегда в native-иерархии, показывается через opacity.
                    // Ключевое: native view НЕ добавляется/удаляется при вводе 3-го символа.
                    <View
                        pointerEvents={showSuggestions ? 'auto' : 'none'}
                        style={[
                            iosAbsoluteBase,
                            {
                                ...(iosSuggestionsPanelHeight != null
                                    ? { height: iosSuggestionsPanelHeight }
                                    : { minHeight: Math.max(windowHeight * 0.45, 240) }),
                                opacity: showSuggestions ? 1 : 0,
                                shadowColor: '#000',
                                shadowOffset: { width: 0, height: 2 },
                                shadowOpacity: showSuggestions ? 0.1 : 0,
                                shadowRadius: 4,
                            },
                        ]}
                    >
                        {suggestionsPanel}
                    </View>
                ) : (isShowingSuggestions && !errorOccurred ? suggestionsPanel : null)}
            </View>
        );
    },
    ({
        placeholder = 'placeholders.search',
        setState,
        suggestionsRequest,
        onSuggestionSelected,
        searchAfter = 0,
        timeout,
        onChangeText = (v) =>
            setState(
                {
                    value: v,
                    isLoading: true,
                    isShowingSuggestions: v.length > searchAfter,
                    errorOccurred: false,
                },
                () => {
                    if (v.length > searchAfter) {
                        if (timeout) {
                            clearTimeout(timeout);
                        }
                        // eslint-disable-next-line no-param-reassign
                        timeout = setTimeout(async () => {
                            suggestionsRequest(v)
                                .then((data) => {
                                    setState({
                                        suggestions: data,
                                        isLoading: false,
                                    });
                                })
                                .catch(() =>
                                    setState({
                                        suggestions: [],
                                        isLoading: false,
                                        errorOccurred: true,
                                    }),
                                );
                        }, 800);
                    } else if (v === '') {
                        onSuggestionSelected('');
                        setState({
                            value: '',
                            suggestions: [],
                            isLoading: false,
                            isShowingSuggestions: false,
                        });
                    }
                },
            ),
    }) => ({
        placeholder: Object.R(placeholder),
        onClear: () => {
            onChangeText('');
            actions.setSearchSuggestionsKeyword('');
        },
        onChangeText,
        setState,
    }),
);
